// 노션 "Markdown & CSV" export zip 파싱.
//
// 노션 export 구조:
//   - 각 페이지는 `제목 <32자리 해시>.md` 파일.
//   - 페이지에 하위 페이지가 있으면 같은 이름의 폴더 `제목 <32자리 해시>/` 가 생기고
//     그 안에 자식 .md 파일들이 들어간다.
//   - 파일명(과 폴더명) 끝의 32자리 hex 해시는 노션 내부 id 이므로 제목에서 제거한다.
//   - CSV 는 DB export 라 페이지로 만들지 않는다. 그 외 비 .md 파일(이미지/html/pdf 등)은
//     "include files" export 의 첨부다 — assets 로 수집해 runImport 가 workspace-uploads 에
//     업로드하고 본문의 상대경로 링크를 public URL(+ 임베드/파일 마커)로 재작성한다 (2026-07-06).
//
// 출력: 평탄화된 노드 배열. 각 노드는 부모 경로(parentPath)를 가지며, 호출부에서
// 폴더 계층 → parent_id 매핑에 사용한다.

import JSZip from 'jszip';

const HASH_RE = /\s*[0-9a-fA-F]{32}$/;

// 파일/폴더 이름에서 노션 해시 + 확장자 제거 후 제목 정리.
export function cleanTitle(name) {
  let base = name.replace(/\.md$/i, '');
  base = base.replace(HASH_RE, '');
  // 노션은 슬래시 등을 공백/유니코드로 치환하기도 함. 트림만 안전하게.
  return base.trim() || '제목 없음';
}

// 경로의 디렉터리 부분(부모 폴더 경로)을 반환. 없으면 '' (루트).
function dirOf(path) {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
}

/**
 * zip(File|Blob|ArrayBuffer) → 페이지 노드 트리 정보 + 첨부 파일 엔트리.
 * @returns {Promise<{ nodes: Array, total: number, assets: Map }>}
 *   node = { id, title, md, dirPath, parentDir }
 *     - id: zip 내부 경로(고유)
 *     - dirPath: 이 페이지가 자식 페이지를 담는 폴더 경로(있으면), 없으면 null
 *     - parentDir: 이 페이지의 부모 폴더 경로('' = 루트)
 *   assets = Map<zip 경로, { entry: JSZip 엔트리, name: 파일명 }>
 *     - .md/.csv 를 제외한 모든 파일(이미지/html/pdf 등) — 본문에서 참조된 것만
 *       runImport 가 업로드한다(참조 판정: collectAssetRefs).
 */
export async function parseNotionZip(input) {
  const zip = await JSZip.loadAsync(input);

  // .md 파일만 페이지 후보로 수집. 그 외 파일(csv 제외)은 첨부 후보.
  const mdEntries = [];
  const assets = new Map();
  zip.forEach((relPath, entry) => {
    if (entry.dir) return;
    if (/\.md$/i.test(relPath)) {
      // 노션 export 는 보통 단일 최상위 폴더(Export-xxxx)로 감싸기도 하고 안 감싸기도 함.
      mdEntries.push({ relPath, entry });
      return;
    }
    // CSV 는 노션 DB export — 첨부가 아니므로 제외.
    if (/\.csv$/i.test(relPath)) return;
    assets.set(relPath, { entry, name: relPath.split('/').pop() || 'file' });
  });

  // 폴더 경로 집합 — 각 .md 와 같은 이름의 폴더가 자식 컨테이너.
  const folderSet = new Set();
  zip.forEach((relPath, entry) => {
    if (entry.dir) {
      folderSet.add(relPath.replace(/\/$/, ''));
    }
  });

  const nodes = [];
  for (const { relPath, entry } of mdEntries) {
    // eslint-disable-next-line no-await-in-loop
    const md = await entry.async('string');
    const withoutExt = relPath.replace(/\.md$/i, '');
    // 같은 이름 폴더가 존재하면 이 페이지의 자식 컨테이너 경로.
    const childDir = folderSet.has(withoutExt) ? withoutExt : null;
    nodes.push({
      id: relPath,
      title: cleanTitle(relPath.split('/').pop()),
      md: stripFrontTitle(md),
      dirPath: childDir,
      parentDir: dirOf(relPath),
    });
  }

  return { nodes, total: nodes.length, assets };
}

/* ─────────────────── 첨부(비 .md 파일) 링크 재작성 ───────────────────
 * 노션 "Markdown & CSV (include files)" export 의 본문 링크는
 * `[파일명](페이지제목%20<해시>/파일명.png)` 처럼 zip 내부 상대경로다.
 * 그대로 import 하면 죽은 링크가 되므로:
 *   1) collectAssetRefs — 본문에서 참조된 첨부의 zip 경로를 수집 (업로드 대상 선별)
 *   2) (runImport 가 workspace-uploads 업로드 → zip 경로 → public URL 매핑)
 *   3) rewriteZipAttachments — 본문 상대경로를 public URL 로 치환.
 *      단독 문단 링크는 노션 직접 가져오기(edge notion-import)와 동일한 마커로 승격:
 *        .html/.htm → :::embed url::: + [파일명](url) 병기
 *        .pdf       → :::embed url:::
 *        그 외 파일  → :::file url | 이름:::
 *      (markdownToYstate 가 마커를 실제 embed/file 블록으로 치환 — 수정 불필요)
 */

// 마크다운 링크/이미지: (!?)[text](target) — target 은 닫는 괄호 전까지(노션은 공백을 %20 인코딩)
const MD_LINK_RE = /(!?)\[([^\]]*)\]\(([^)]+)\)/g;

// edge notion-import 의 markerSafeUrl/markerSafeName 과 동일 규칙 (마커 정규식 안전).
// URL 안의 '_'/'*' 등 마크다운-활성 문자는 markdownToYstate 의 extractMarkerPayloads 가
// 파싱 *전에* 원본 그대로 추출·보관하므로(2026-07-06 근본 수정) 여기서 걸러낼 필요 없다
// — 걸러내면 밑줄 든 유튜브/피그마 URL 이 임베드 대신 링크로 강등된다.
function markerSafeUrl(u) {
  return /^\S+$/.test(u) && !u.includes(':::');
}
function markerSafeName(n) {
  return String(n || '').replace(/:{3,}/g, ':').replace(/\s+/g, ' ').trim();
}

// `a/./b`, `a/../b` 정규화 (zip 경로는 '/' 구분 고정)
function normalizePath(p) {
  const parts = [];
  for (const seg of String(p).split('/')) {
    if (!seg || seg === '.') continue;
    if (seg === '..') { parts.pop(); continue; }
    parts.push(seg);
  }
  return parts.join('/');
}

/**
 * 본문 링크 target(상대경로) → zip 첨부 경로 매칭.
 * 규칙: URL 디코드 → 페이지 폴더(baseDir) 기준 정확 경로 매칭 → 실패 시 파일명 단독 폴백
 * (폴백은 baseDir 하위 항목 우선). 절대 URL(스킴/`//`/`/`)·앵커는 첨부가 아니므로 null.
 * @returns {string|null} assets 의 키(zip 경로)
 */
export function resolveAssetPath(target, baseDir, assets) {
  if (!target || !assets || !assets.size) return null;
  // 앵커/쿼리 분리 (노션 export 상대경로에는 없지만 방어)
  const t = String(target).trim().split('#')[0].split('?')[0];
  if (!t) return null;
  // 절대 URL / 루트 경로 — 첨부 상대경로가 아님
  if (/^[a-z][a-z0-9+.-]*:/i.test(t) || t.startsWith('//') || t.startsWith('/')) return null;
  let decoded = t;
  try { decoded = decodeURIComponent(t); } catch (_e) { /* 손상 인코딩 — 원문 그대로 매칭 시도 */ }
  const full = normalizePath(baseDir ? `${baseDir}/${decoded}` : decoded);
  if (assets.has(full)) return full;
  // 폴백: 파일명 단독 매칭 (같은 페이지 폴더 하위 우선)
  const base = decoded.split('/').pop();
  if (!base) return null;
  let anyMatch = null;
  for (const key of assets.keys()) {
    if ((key.split('/').pop() || '') !== base) continue;
    if (baseDir && key.startsWith(`${baseDir}/`)) return key;
    if (!anyMatch) anyMatch = key;
  }
  return anyMatch;
}

/**
 * 본문에서 참조된 첨부의 zip 경로 집합 — 업로드 대상 선별용 (미참조 첨부는 업로드 안 함).
 * @returns {Set<string>}
 */
export function collectAssetRefs(md, node, assets) {
  const refs = new Set();
  if (!md || !assets || !assets.size) return refs;
  for (const m of md.matchAll(MD_LINK_RE)) {
    const p = resolveAssetPath(m[3], node.parentDir, assets);
    if (p) refs.add(p);
  }
  return refs;
}

/**
 * 본문의 첨부 상대경로를 업로드된 public URL 로 재작성.
 * - 단독 문단 링크(앞뒤 빈 줄, `[..](..)` 만) → 임베드/파일 마커 승격 (html/pdf/기타)
 * - 이미지 `![..](..)` → URL 치환만 (BlockNote image 블록 파싱은 기존 동작)
 * - 인라인(문장 중간) 링크 → URL 치환만 (문장 훼손 금지)
 * - 업로드 실패(urlByPath 에 없음) → 원문 유지 (오류는 runImport 가 수집)
 * @param {string} md
 * @param {{ parentDir: string }} node
 * @param {Map} assets - parseNotionZip 의 assets
 * @param {Map<string, string>} urlByPath - zip 경로 → public URL
 */
export function rewriteZipAttachments(md, node, assets, urlByPath) {
  if (!md || !assets || !assets.size || !urlByPath || !urlByPath.size) return md;
  const lines = md.split('\n');
  const out = [];
  const isBlank = (s) => !s || !s.trim();

  const resolvePub = (target) => {
    const path = resolveAssetPath(target, node.parentDir, assets);
    if (!path) return null;
    const pub = urlByPath.get(path);
    return pub ? { path, pub } : null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    // 단독 문단 판정은 원본 라인 기준(치환으로 삽입된 빈 줄에 오염되지 않게)
    const soloM = line.trim().match(/^\[([^\]]*)\]\(([^)]+)\)$/);
    if (soloM) {
      const prevBlank = i === 0 || isBlank(lines[i - 1]);
      const nextBlank = i === lines.length - 1 || isBlank(lines[i + 1]);
      const hit = prevBlank && nextBlank ? resolvePub(soloM[2]) : null;
      if (hit && markerSafeUrl(hit.pub)) {
        const name = soloM[1].trim() || hit.path.split('/').pop() || 'file';
        const ext = ((hit.path.match(/\.([A-Za-z0-9]+)$/) || [])[1] || '').toLowerCase();
        // .html — 임베드(미리보기) + 파일명 링크 병기 (edge notion-import 와 동일)
        if (ext === 'html' || ext === 'htm') {
          out.push(`:::embed ${hit.pub}:::`, '', `[${name}](${hit.pub})`);
          continue;
        }
        // .pdf — Embed 블록이 브라우저 내장 PDF 뷰어로 렌더
        if (ext === 'pdf') { out.push(`:::embed ${hit.pub}:::`); continue; }
        // 그 외 파일 — 다운로드 카드(file 블록)
        const safeName = markerSafeName(name);
        out.push(safeName ? `:::file ${hit.pub} | ${safeName}:::` : `:::file ${hit.pub}:::`);
        continue;
      }
      // 단독이지만 마커 불가(업로드 실패/URL 비안전) → 아래 인라인 치환 경로로 폴백
    }
    // 인라인 링크·이미지 — URL 치환만 (블록 구조 유지)
    out.push(line.replace(MD_LINK_RE, (full, bang, text, target) => {
      const hit = resolvePub(target);
      if (!hit) return full; // 매칭/업로드 실패 — 원문(기존 상대경로) 유지
      return `${bang}[${text}](${hit.pub})`;
    }));
  }
  return out.join('\n');
}

// 노션 .md 의 첫 줄은 보통 `# 제목` — 제목은 페이지 title 로 이미 쓰므로 본문 중복 제거.
function stripFrontTitle(md) {
  if (!md) return md;
  const lines = md.split('\n');
  let i = 0;
  // 선행 공백 줄 스킵
  while (i < lines.length && lines[i].trim() === '') i += 1;
  if (i < lines.length && /^#\s+/.test(lines[i])) {
    lines.splice(0, i + 1);
    // 제목 뒤 공백 줄도 한 줄 정리
    while (lines.length && lines[0].trim() === '') lines.shift();
    return lines.join('\n');
  }
  return md;
}

/**
 * 노드 배열 → 부모 폴더 경로 기준 트리 순서(부모 먼저)로 정렬.
 * 부모 페이지의 dirPath 와 자식 페이지의 parentDir 가 일치하면 부모-자식 관계.
 */
export function orderForImport(nodes) {
  // dirPath(자식 컨테이너) → 부모 노드 매핑
  const containerToParent = new Map();
  for (const n of nodes) {
    if (n.dirPath) containerToParent.set(n.dirPath, n);
  }
  // 각 노드의 부모 노드(있으면) 계산
  for (const n of nodes) {
    n.parentNode = containerToParent.get(n.parentDir) || null;
  }
  // 부모가 먼저 오도록 위상 정렬(깊이 우선)
  const ordered = [];
  const visited = new Set();
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const visit = (node) => {
    if (visited.has(node.id)) return;
    if (node.parentNode && !visited.has(node.parentNode.id)) {
      visit(node.parentNode);
    }
    visited.add(node.id);
    ordered.push(node);
  };
  for (const n of nodes) visit(byId.get(n.id) || n);
  return ordered;
}
